import { Button, Table, Tag } from '@ph-react-ui/core';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../hooks/auth/useAuth';
import { usePaginatedFoodTaxes } from '../../../../hooks/taxes/food/admin/usePaginatedFoodTaxes';
import { ScholarshipTranslations } from '../../../../models/users/students/scholarship-type';
import { SCHOOL_YEAR_START_MONTH } from '../../../../utils/constants/food-management/schoolYearStartMonth';
import { displayPrice } from '../../../../utils/displayPrice';
import type { Currency } from '../../../../utils/enums/currency-type.enum';
import { ScopeAccess } from '../../../../utils/enums/scope-access.enum';
import { EmptyState } from '../../../shared/components/EmptyState';
import { MonthPagination } from '../../../shared/components/MonthPagination';
import { PageTitle } from '../../../shared/components/PageTitle';
import { Pagination } from '../../../shared/components/Pagination';
import { MonthPaginationSkeleton } from '../../../shared/components/skeletons/MonthPaginationSkeleton';
import { TableSkeleton } from '../../../shared/components/skeletons/TableSkeleton';
import { TaxesFilters } from '../../../shared/components/TaxesFilters';

export function ParentFoodTaxesHistory() {
  const { authState } = useAuth();
  const canMakePayments = authState?.scope?.includes(ScopeAccess.MakePayments);
  const match = useMatch('/dashboard/payments/food/history');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get('month');
  const yearParam = searchParams.get('year');

  const getCurrentSchoolYearStart = useCallback(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    return currentMonth >= SCHOOL_YEAR_START_MONTH
      ? currentYear
      : currentYear - 1;
  }, []);

  useEffect(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    const currentSchoolYearStart = getCurrentSchoolYearStart();

    if (!monthParam || !yearParam) {
      searchParams.set('month', currentMonth.toString());
      searchParams.set('year', currentYear.toString());
      setSearchParams(searchParams);
      return;
    }

    const yearParamNum = Number(yearParam);
    const monthParamNum = Number(monthParam);

    if (
      yearParamNum < currentSchoolYearStart ||
      (yearParamNum === currentSchoolYearStart &&
        monthParamNum < SCHOOL_YEAR_START_MONTH)
    ) {
      searchParams.set('month', SCHOOL_YEAR_START_MONTH.toString());
      searchParams.set('year', currentSchoolYearStart.toString());
      setSearchParams(searchParams);
    }
  }, [
    monthParam,
    yearParam,
    searchParams,
    setSearchParams,
    getCurrentSchoolYearStart,
  ]);

  const {
    data: foodTaxes,
    isSuccess,
    isPending,
    isError,
  } = usePaginatedFoodTaxes(searchParams);

  const headers = useMemo(() => {
    const baseHeaders = [
      t('USERS.TABLE.HEADERS.NAME'),
      t('TAXES.TABLE.HEADERS.TYPE'),
      t('TAXES.TABLE.HEADERS.AMOUNT'),
      t('TAXES.TABLE.HEADERS.STATUS'),
      t('TAXES.TABLE.HEADERS.PAID_WITH'),
    ];
    if (canMakePayments) {
      baseHeaders.push(t('TAXES.TABLE.HEADERS.ACTIONS'));
    }
    return baseHeaders;
  }, [t, canMakePayments]);

  const onPayNow = useCallback(
    (
      studentId: string,
      mealMenuId: string,
      month: number,
      year: number,
      amount: number,
      currency: Currency
    ) => {
      navigate(
        `/dashboard/payments/monthly-menu/pre-payment/${studentId}/${month}/${year}`,
        {
          state: {
            mealMenuIds: [mealMenuId],
            paymentItems: [{ mealMenuId, month, year, amount, currency }],
          },
        }
      );
    },
    [navigate]
  );

  const canGoToPrevMonth = useMemo(() => {
    if (!monthParam || !yearParam) return false;

    const currentSchoolYearStart = getCurrentSchoolYearStart();
    const yearParamNum = Number(yearParam);
    const monthParamNum = Number(monthParam);

    if (yearParamNum > currentSchoolYearStart) return true;

    if (yearParamNum === currentSchoolYearStart) {
      return monthParamNum > SCHOOL_YEAR_START_MONTH;
    }

    return false;
  }, [monthParam, yearParam, getCurrentSchoolYearStart]);

  const tableChildren = useMemo(() => {
    if (!foodTaxes?.result) return [];

    return foodTaxes.result.reduce((acc: any, user) => {
      let userArray = [
        user.name,
        t(`TAXES.LABELS.${ScholarshipTranslations[user.scholarshipType!]}`),
        <span
          className={`${
            !user.isPaid ? 'taxes__transportation__table__row-danger' : ''
          }`}
        >
          {displayPrice(
            user.amount,
            t('PAYMENTS.LABELS.BGN'),
            t('PAYMENTS.LABELS.EUR'),
            user.currency
          )}
        </span>,
        user.isPaid ? (
          <Tag type="success">{t('TAXES.LABELS.PAID')}</Tag>
        ) : (
          <Tag type="danger">{t('TAXES.LABELS.NOT_PAID')}</Tag>
        ),
        user.isPaid
          ? user.isPaidWithCard
            ? t('TAXES.LABELS.CARD')
            : t('TAXES.LABELS.TRANSFER')
          : '',
      ];

      if (canMakePayments) {
        if (!user.isPaid) {
          userArray.push(
            <Button
              variant="outlined"
              onClick={() =>
                onPayNow(
                  user.id,
                  foodTaxes.mealMenuId!,
                  foodTaxes.month,
                  foodTaxes.year,
                  user.amount,
                  user.currency
                )
              }
            >
              {t('ACTIONS.PAY_NOW')}
            </Button>
          );
        } else {
          userArray.push('');
        }
      }

      return [...acc, userArray];
    }, []);
  }, [
    foodTaxes?.result,
    foodTaxes?.mealMenuId,
    foodTaxes?.month,
    foodTaxes?.year,
    t,
    onPayNow,
    canMakePayments,
  ]);

  return (
    <div className="taxes__food">
      <PageTitle
        title={t('TAXES.TITLES.SCHOOL_TAXES_HISTORY')}
        subtitle={t('TAXES.TITLES.FOOD')}
      />
      <div className="taxes__food__month__pagination">
        {!isPending ? (
          <MonthPagination
            month={foodTaxes?.month.toString() || monthParam}
            year={foodTaxes?.year.toString() || yearParam}
            canGoToPrevMonth={canGoToPrevMonth}
          />
        ) : (
          <MonthPaginationSkeleton />
        )}
      </div>
      <div className="taxes__food__filter">
        <TaxesFilters
          pathMatch={match}
          hideFromToDatePickers={true}
          showChildDropdown={true}
          searchByName={true}
          filterUsingRestaurantOnly={true}
        />
      </div>
      {isPending && <TableSkeleton />}

      {isSuccess &&
        foodTaxes &&
        (foodTaxes.totalCount > 0 ? (
          <>
            <Table headers={headers} rows={tableChildren} />
            <div className="users__pagination">
              <Pagination data={foodTaxes} />
            </div>
          </>
        ) : (
          <EmptyState message={t('TAXES.MESSAGES.NO_DATA_FOR_CURRENT_MONTH')} />
        ))}

      {isError && (
        <EmptyState message={t('TAXES.MESSAGES.NO_DATA_FOR_CURRENT_MONTH')} />
      )}
    </div>
  );
}
